import {
  createGoogleMapsPinMarkerIcon,
  createGoogleMapsRingMarkerIcon,
} from '~/app/lib/google-maps-presets';
import type { SiteOperationsMapSite } from '~/app/services/site/site.service';
import type {
  HoveredLocation,
  MapLayer,
  MarkerEntry,
  SupervisorOperationsMapData,
} from '~/app/dashboard/components/SupervisorOperationsMapCard.types';

export function getOperationalColor(summary: {
  criticalAssets: number;
  warningAssets: number;
}): string {
  if (summary.criticalAssets > 0) {
    return '#dc2626';
  }

  if (summary.warningAssets > 0) {
    return '#f59e0b';
  }

  return '#16a34a';
}

export function getOperationalTagColor(summary: {
  criticalAssets: number;
  warningAssets: number;
}): 'red' | 'gold' | 'green' {
  const color = getOperationalColor(summary);

  if (color === '#dc2626') {
    return 'red';
  }

  if (color === '#f59e0b') {
    return 'gold';
  }

  return 'green';
}

export function getMapZoom(markers: MarkerEntry[]): number {
  if (markers.length <= 1) {
    return 13;
  }

  const latitudes = markers.map((marker) => marker.position.lat);
  const longitudes = markers.map((marker) => marker.position.lng);
  const latSpread = Math.max(...latitudes) - Math.min(...latitudes);
  const lngSpread = Math.max(...longitudes) - Math.min(...longitudes);
  const spread = Math.max(latSpread, lngSpread);

  if (spread < 0.03) {
    return 13;
  }

  if (spread < 0.12) {
    return 11;
  }

  if (spread < 0.5) {
    return 9;
  }

  if (spread < 1.5) {
    return 8;
  }

  return 6;
}

export function formatSiteLocation(site: SiteOperationsMapSite): string {
  const parts = [site.city?.name, site.province?.name].filter(Boolean);

  if (parts.length > 0) {
    return parts.join(', ');
  }

  return site.address ?? 'No address available';
}

export function buildSupervisorOperationsMapMarkers(
  data: SupervisorOperationsMapData,
  layer: MapLayer,
): MarkerEntry[] {
  const siteMarkers = data.sites.flatMap((site) => {
    if (site.latitude === null || site.longitude === null) {
      return [];
    }

    const color = getOperationalColor(site);

    return [
      {
        key: `site-${site.id}`,
        kind: 'site' as const,
        position: { lat: site.latitude, lng: site.longitude },
        title: `${site.name} · ${site.totalOpenIncidents} open incidents`,
        icon: createGoogleMapsRingMarkerIcon(color),
        zIndex: 20,
        href: `/sites/${site.id}`,
        payload: {
          kind: 'site' as const,
          data: site,
        },
      },
    ];
  });

  const zoneMarkers = data.zones.flatMap((zone) => {
    if (zone.latitude === null || zone.longitude === null) {
      return [];
    }

    const color = getOperationalColor(zone);

    return [
      {
        key: `zone-${zone.id}`,
        kind: 'zone' as const,
        position: { lat: zone.latitude, lng: zone.longitude },
        title: `${zone.name} · ${zone.totalOpenIncidents} open incidents`,
        icon: createGoogleMapsPinMarkerIcon(color),
        zIndex: zone.isMain ? 30 : 10,
        href: `/sites/${zone.siteId}/zones/${zone.id}`,
        payload: {
          kind: 'zone' as const,
          data: zone,
        },
      },
    ];
  });

  if (layer === 'sites') {
    return siteMarkers;
  }

  if (layer === 'zones') {
    return zoneMarkers;
  }

  return [...siteMarkers, ...zoneMarkers];
}

export function getMissingCoordinatesCount(
  data: SupervisorOperationsMapData,
  layer: MapLayer,
): number {
  const siteMissing = data.sites.filter(
    (site) => site.latitude === null || site.longitude === null,
  ).length;
  const zoneMissing = data.zones.filter(
    (zone) => zone.latitude === null || zone.longitude === null,
  ).length;

  if (layer === 'sites') {
    return siteMissing;
  }

  if (layer === 'zones') {
    return zoneMissing;
  }

  return siteMissing + zoneMissing;
}

export function getActiveHoveredLocation(
  hoveredLocation: HoveredLocation | null,
  markers: MarkerEntry[],
): HoveredLocation | null {
  if (!hoveredLocation) {
    return markers[0]?.payload ?? null;
  }

  const matchingMarker = markers.find(
    (marker) =>
      marker.payload.kind === hoveredLocation.kind &&
      marker.payload.data.id === hoveredLocation.data.id,
  );

  return matchingMarker?.payload ?? markers[0]?.payload ?? null;
}

export function getLocationOpenHref(location: HoveredLocation): string {
  if (location.kind === 'site') {
    return `/sites/${location.data.id}`;
  }

  return `/sites/${location.data.siteId}/zones/${location.data.id}`;
}

export function getParentSiteHref(siteId: string): string {
  return `/sites/${siteId}`;
}
